import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Flame, LogOut, History, LayoutDashboard, Sparkles, Activity, Leaf } from "lucide-react";

export function Brand({ size = "md" }) {
  const s = size === "lg" ? "text-3xl" : "text-xl";
  return (
    <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
      <span className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center shrink-0">
        <Flame className="w-5 h-5 text-primary-foreground" />
      </span>
      <span className={`font-heading font-black tracking-tight ${s} text-foreground`}>
        Sugar<span className="text-primary">Burn</span>
      </span>
    </Link>
  );
}

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Today", testid: "tab-today" },
  { to: "/gut", icon: Activity, label: "Gut", testid: "tab-gut" },
  { to: "/herbs", icon: Leaf, label: "Herbs", testid: "tab-herbs" },
  { to: "/history", icon: History, label: "History", testid: "tab-history" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen pb-28 md:pb-10">
      <header className="sticky top-0 z-40 bg-background/95 border-b border-border backdrop-blur">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between gap-4">
          <Brand />
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => {
              const Icon = n.icon;
              const active = pathname === n.to;
              return (
                <button
                  key={n.to}
                  onClick={() => navigate(n.to)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold transition-colors ${active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-primary"}`}
                  data-testid={`desktop-${n.testid}`}
                >
                  <Icon className="w-4 h-4" /> {n.label}
                </button>
              );
            })}
          </nav>
          <div className="flex items-center gap-2">
            {!user?.is_premium && (
              <Button size="sm" onClick={() => navigate("/pricing")} className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold" data-testid="nav-upgrade-btn">
                <Sparkles className="w-4 h-4 mr-1" /> Go Premium
              </Button>
            )}
            {user?.is_premium && (
              <span className="hidden sm:inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-secondary px-3 py-1 rounded-full bg-secondary/10" data-testid="premium-badge">
                <Sparkles className="w-3 h-3" /> Premium
              </span>
            )}
            <Button variant="ghost" size="icon" onClick={doLogout} className="rounded-full" data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-8">{children}</main>

      <nav className="fixed bottom-0 inset-x-0 z-40 bg-card border-t border-border md:hidden">
        <div className="flex justify-around items-center h-16">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = pathname === n.to;
            return (
              <button key={n.to} onClick={() => navigate(n.to)} className={`flex flex-col items-center gap-1 transition-colors px-3 ${active ? "text-primary" : "text-muted-foreground"}`} data-testid={n.testid}>
                <Icon className="w-5 h-5" />
                <span className="text-xs font-semibold">{n.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
